import { Space, Tooltip, Button, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import type { TFunction } from 'i18next'

import type { ReceivedCancellationRequestRoot } from '@repo/sdk'

import { ProductCell } from '../../components/ProductCell'
import { StatusBadge, type StatusConfig } from '../../components/StatusBadge'
import { UserAvatar } from '../../components/UserAvatar'
import { NicknameText } from '../../components/NicknameText'

import type { CancellationStatus } from './cancellationStatusConfig'
import type { CancellationReasonType } from './cancellationReasonConfig'
import { formatOrderBriefLabel } from '../orders/orderDisplay'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

type CancellationStatusConfigMap = Record<CancellationStatus, StatusConfig>

export interface CancellationListColumnProps {
  onViewDetail: (cancellationId: string) => void
  t: TFunction
  statusConfig: CancellationStatusConfigMap
  reasonLabels: Record<CancellationReasonType, string>
}

export function createCancellationListColumns({
  onViewDetail,
  t,
  statusConfig,
  reasonLabels,
}: CancellationListColumnProps): ColumnType<ReceivedCancellationRequestRoot>[] {
  return [
    {
      title: t('common.columns.cancellationId'),
      dataIndex: 'cancellationId',
      key: 'cancellationId',
      width: 120,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => (
        <Tooltip title={record.cancellationId}>
          <Button
            data-testid={`cancellation-list-id-link-${record.cancellationId}`}
            type="link"
            size="small"
            onClick={() => onViewDetail(record.cancellationId)}
          >
            {record.cancellationId.slice(0, 8)}...
          </Button>
        </Tooltip>
      ),
    },
    {
      title: t('common.columns.order'),
      key: 'orderId',
      width: 300,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => (
        <Typography.Text>{formatOrderBriefLabel(record.order)}</Typography.Text>
      ),
    },
    {
      title: t('common.columns.productName'),
      key: 'product',
      width: 200,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => (
        <ProductCell
          name={record.orderItems?.[0]?.productName}
          thumbnailUrl={record.orderItems?.[0]?.productImageUrl}
        />
      ),
    },
    {
      title: t('common.columns.buyer'),
      key: 'buyer',
      width: 150,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => (
        <Space>
          <UserAvatar
            src={record.buyer.profileImageUrl ?? undefined}
            nickname={record.buyer.nickname}
            size="small"
          />
          <NicknameText nickname={record.buyer.nickname} />
        </Space>
      ),
    },
    {
      title: t('common.columns.cancellationReason'),
      key: 'reasonType',
      width: 150,
      ellipsis: true,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => {
        const label = reasonLabels[record.reasonType] ?? record.reasonType
        return (
          <Tooltip title={label}>
            <Typography.Text ellipsis>{label}</Typography.Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('common.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => (
        <StatusBadge status={record.status} configMap={statusConfig} />
      ),
    },
    {
      title: t('common.columns.requestDate'),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 150,
      render: (value: string) => (value ? formatKstDateTime(value, 'YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: t('common.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: unknown, record: ReceivedCancellationRequestRoot) => (
        <Tooltip title={t('common.buttons.view')}>
          <Button
            data-testid={`cancellation-list-view-btn-${record.cancellationId}`}
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(record.cancellationId)}
          />
        </Tooltip>
      ),
    },
  ]
}
